import { Trans } from '@lingui/react/macro';
import { Backdrop, Box, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import LockIcon from '../../custom-icons/LockIcon';
import { useUser } from '../../hooks/useUser';
import { appRoutes } from '../../routes';
import { useGetPolicyDocumentQuery } from '../../services/portal/portalApi';

const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, auth, nav } = useUser();
  const theme = useTheme();
  const { data: policyDocument, isLoading: isPolicyDocumentLoading } = useGetPolicyDocumentQuery();
  const userPolicyDefinitions = useMemo(
    () => Object.keys(policyDocument?.policy_definitions || []),
    [policyDocument]
  );

  const currentRoute = appRoutes.find(({ path }) => {
    try {
      return matchPath({ path }, location.pathname);
    } catch (error) {
      console.log('Error matching path:', error);
      return false;
    }
  });
  const isProtected = currentRoute?.protected ?? false;

  const policies = useMemo(() => currentRoute?.policies ?? [], [currentRoute]);

  const loggedIn = useMemo(() => {
    return !isProtected || (isAuthenticated && isProtected);
  }, [isAuthenticated, isProtected]);

  const hasAccess = useMemo(() => {
    if (policies.length === 0 || isPolicyDocumentLoading) return true; // No policies means any authenticated user can access
    return policies.every((policy) => userPolicyDefinitions.includes(policy));
  }, [isPolicyDocumentLoading, policies, userPolicyDefinitions]);

  const navigate = useNavigate();

  useEffect(() => {
    if (isProtected && !isAuthenticated && !isLoading) {
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [isProtected, isAuthenticated, isLoading, navigate, location, auth]);

  if (isProtected && !isAuthenticated && isLoading) {
    // Show a loading state while checking authentication
    return (
      <Backdrop open sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ position: 'absolute' }} size={60} />
      </Backdrop>
    );
  }

  if (!loggedIn) {
    // Redirect to login or show an error
    return (
      <div>
        <Trans>Redirecting to login</Trans>
      </div>
    );
  }
  if (loggedIn && !hasAccess) {
    return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 120px)',
          px: { xs: 2, sm: 4 },
          py: { xs: 4, sm: 6 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 560,
            p: { xs: 3, sm: 4 },
            borderRadius: '10px',
            background: theme.palette.background.paperElevation0,
            boxShadow: theme.palette.shadows[25],
            border: `1px solid ${alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.28 : 0.16)}`,
          }}
        >
          <Stack spacing={3} alignItems="flex-start">
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                color: 'error.main',
                backgroundColor: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === 'dark' ? 0.2 : 0.1
                ),
              }}
            >
              <LockIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h2" sx={{ mb: 1 }}>
                <Trans>Access denied</Trans>
              </Typography>
              <Typography variant="body1" color="text.secondary">
                <Trans>
                  You do not have permission to view this page. If you need access, contact your
                  administrator or return to an available section.
                </Trans>
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  }
  if (loggedIn) {
    return children;
  }
  return <></>;
};

export default AuthWrapper;
